;(function () {
  class CampaignModelSelectorFeatureCards {
    constructor(root) {
      this.root = root
      this.cards = Array.from(
        root.querySelectorAll('.campaign-model-selector-feature-cards__card')
      )
      this.selectorButtons = Array.from(
        root.querySelectorAll('[data-model-selector-trigger]')
      )
      this.featureTriggers = Array.from(
        root.querySelectorAll('[data-feature-trigger]')
      )
      this.popover = root.querySelector('[data-feature-popover]')
      this.popoverTitle = root.querySelector('[data-feature-popover-title]')
      this.popoverText = root.querySelector('[data-feature-popover-text]')
      this.featureDefinitions = this.parseFeatureDefinitions()
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)')
      this.selectedModelKey = null
      this.activeTrigger = null

      if (this.cards.length === 0) return

      this.handleDocumentClick = this.handleDocumentClick.bind(this)
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
      this.handleWindowResize = this.handleWindowResize.bind(this)
      this.handleDocumentScroll = this.handleDocumentScroll.bind(this)

      this.bindSelector()
      this.bindFeatures()
      this.init()
    }

    parseFeatureDefinitions() {
      const node = this.root.querySelector('[data-feature-definitions]')
      if (!node) return {}

      try {
        return JSON.parse(node.textContent)
      } catch (error) {
        return {}
      }
    }

    isMobile() {
      return this.mobileMediaQuery.matches
    }

    getInitialModelKey() {
      const currentCard = this.cards.find(
        (card) => card.dataset.currentModel === 'true'
      )

      if (currentCard) return currentCard.dataset.modelKey

      const recommendedCard = this.cards.find(
        (card) => card.dataset.recommended === 'true'
      )

      if (recommendedCard) return recommendedCard.dataset.modelKey

      return this.cards[0]?.dataset.modelKey || null
    }

    updateSelectionState() {
      this.cards.forEach((card) => {
        const isSelected = card.dataset.modelKey === this.selectedModelKey
        const shouldShow = !this.isMobile() || isSelected

        card.classList.toggle('is-selected', isSelected)
        card.hidden = !shouldShow
      })

      this.selectorButtons.forEach((button) => {
        const isSelected = button.dataset.modelKey === this.selectedModelKey

        button.classList.toggle('is-active', isSelected)
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false')
        button.setAttribute('tabindex', isSelected ? '0' : '-1')
      })

      if (
        this.activeTrigger &&
        !this.activeTrigger.closest(
          `.campaign-model-selector-feature-cards__card[data-model-key="${this.selectedModelKey}"]`
        )
      ) {
        this.closePopover()
      }
    }

    selectModel(modelKey) {
      if (!modelKey) return

      this.selectedModelKey = modelKey
      this.updateSelectionState()
    }

    moveSelection(direction) {
      if (this.selectorButtons.length === 0) return

      const currentIndex = this.selectorButtons.findIndex(
        (button) => button.dataset.modelKey === this.selectedModelKey
      )
      const startIndex = currentIndex === -1 ? -direction : currentIndex
      const nextIndex =
        (startIndex + direction + this.selectorButtons.length) %
        this.selectorButtons.length
      const nextButton = this.selectorButtons[nextIndex]

      if (!nextButton) return

      this.selectModel(nextButton.dataset.modelKey)
      nextButton.focus()
    }

    bindSelector() {
      this.selectorButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.closePopover()
          this.selectModel(button.dataset.modelKey)
        })

        button.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault()
            this.moveSelection(1)
          }

          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault()
            this.moveSelection(-1)
          }

          if (event.key === 'Home') {
            event.preventDefault()
            const firstButton = this.selectorButtons[0]
            if (!firstButton) return

            this.selectModel(firstButton.dataset.modelKey)
            firstButton.focus()
          }

          if (event.key === 'End') {
            event.preventDefault()
            const lastButton = this.selectorButtons[this.selectorButtons.length - 1]
            if (!lastButton) return

            this.selectModel(lastButton.dataset.modelKey)
            lastButton.focus()
          }
        })
      })
    }

    bindFeatures() {
      if (!this.popover) return

      this.featureTriggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => {
          if (this.isMobile()) return
          this.openPopover(trigger)
        })

        trigger.addEventListener('mouseleave', () => {
          if (this.isMobile()) return
          this.closePopover()
        })

        trigger.addEventListener('focusin', () => {
          this.openPopover(trigger)
        })

        trigger.addEventListener('focusout', () => {
          if (this.isMobile()) return

          window.requestAnimationFrame(() => {
            if (document.activeElement !== trigger) {
              this.closePopover()
            }
          })
        })

        trigger.addEventListener('click', (event) => {
          if (!this.isMobile()) return

          event.preventDefault()
          event.stopPropagation()

          if (this.activeTrigger === trigger) {
            this.closePopover()
            return
          }

          this.openPopover(trigger)
        })
      })

      document.addEventListener('click', this.handleDocumentClick)
      document.addEventListener('keydown', this.handleDocumentKeydown)
      document.addEventListener('scroll', this.handleDocumentScroll, {
        passive: true,
      })
      window.addEventListener('resize', this.handleWindowResize)
    }

    getFeatureData(featureKey) {
      return this.featureDefinitions[featureKey] || null
    }

    setActiveTrigger(trigger) {
      if (this.activeTrigger && this.activeTrigger !== trigger) {
        this.activeTrigger.classList.remove('is-active-feature')
      }

      this.activeTrigger = trigger || null

      if (this.activeTrigger) {
        this.activeTrigger.classList.add('is-active-feature')
      }
    }

    openPopover(trigger) {
      const featureKey = trigger.dataset.featureKey
      const featureData = this.getFeatureData(featureKey)

      if (!featureData) return

      this.setActiveTrigger(trigger)
      this.popoverTitle.textContent =
        featureData.title || featureData.label || ''
      this.popoverText.textContent = featureData.text || ''
      this.popover.classList.add('is-visible')
      this.popover.setAttribute('aria-hidden', 'false')

      this.positionPopover(trigger)
    }

    positionPopover(trigger) {
      if (!trigger || !this.popover) return

      const rootRect = this.root.getBoundingClientRect()
      const triggerRect = trigger.getBoundingClientRect()
      const popoverRect = this.popover.getBoundingClientRect()
      const spaceBelow = window.innerHeight - triggerRect.bottom
      const openAbove =
        spaceBelow < popoverRect.height + 16 &&
        triggerRect.top > popoverRect.height + 16

      const preferredTop = openAbove
        ? triggerRect.top - rootRect.top - popoverRect.height - 8
        : triggerRect.bottom - rootRect.top + 8

      const centeredLeft =
        triggerRect.left -
        rootRect.left +
        triggerRect.width / 2 -
        popoverRect.width / 2

      const maxLeft = Math.max(0, this.root.clientWidth - popoverRect.width)
      const left = Math.max(0, Math.min(centeredLeft, maxLeft))

      this.popover.style.top = `${Math.max(0, preferredTop)}px`
      this.popover.style.left = `${left}px`
    }

    closePopover() {
      if (!this.popover) return

      this.setActiveTrigger(null)
      this.popover.classList.remove('is-visible')
      this.popover.setAttribute('aria-hidden', 'true')
    }

    handleDocumentClick(event) {
      if (!this.root.contains(event.target)) {
        this.closePopover()
        return
      }

      if (!event.target.closest('[data-feature-trigger]')) {
        this.closePopover()
      }
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopover()
      }
    }

    handleWindowResize() {
      if (!this.selectedModelKey) {
        this.selectedModelKey = this.getInitialModelKey()
      }

      this.updateSelectionState()

      if (this.activeTrigger) {
        this.positionPopover(this.activeTrigger)
      }
    }

    handleDocumentScroll() {
      if (this.activeTrigger) {
        this.closePopover()
      }
    }

    init() {
      this.selectedModelKey = this.getInitialModelKey()
      this.updateSelectionState()
    }
  }

  const initCampaignModelSelectorFeatureCards = (scope) => {
    const roots = []

    if (
      scope instanceof Element &&
      scope.matches('[data-campaign-model-selector-feature-cards]')
    ) {
      roots.push(scope)
    }

    roots.push(
      ...Array.from(
        scope.querySelectorAll('[data-campaign-model-selector-feature-cards]')
      )
    )

    roots.forEach((root) => {
      if (root.dataset.featureCardsInitialized === 'true') return
      root.dataset.featureCardsInitialized = 'true'
      new CampaignModelSelectorFeatureCards(root)
    })
  }

  document.addEventListener('DOMContentLoaded', () => {
    initCampaignModelSelectorFeatureCards(document)
  })

  document.addEventListener('shopify:section:load', (event) => {
    initCampaignModelSelectorFeatureCards(event.target)
  })
})()
